"use client"

import React, { useMemo, useState } from "react"
import { ArrowUpDown, Search } from "lucide-react"
import {
  getIterations,
  num,
  loadIterationToGraph,
} from "../utils/iterationsHelpers"
import { formatDateTime } from "../utils/runsHelpers"
import {
  MultiMetricLineChart,
  ScrollableBarChartWithAxes,
} from "./RunsCharts"

export function IterationsModal({ open, run, onClose, datasetById }) {
  const [itersQuery, setItersQuery] = useState("")
  const [itersSort, setItersSort] = useState({ key: "iteration", dir: "asc" })

  const itersRaw = useMemo(
    () => (run ? getIterations(run) : []),
    [run],
  )

  const itersFilteredSorted = useMemo(() => {
    let arr = itersRaw.map((it, idx) => ({
      ...it,
      _rowId: `${run?.id || "run"}_${idx}`,
      _nodesF1: Number(it?.metrics?.nodes?.f1Score) || 0,
      _edgesF1: Number(it?.metrics?.edges?.f1Score) || 0,
      _nodeTypes:
        it?.summary?.nodeTypes ?? (it?.snapshot?.nodes?.length ?? undefined),
      _edgesCnt:
        it?.summary?.edges ?? (it?.snapshot?.edges?.length ?? undefined),
    }))
    if (itersQuery.trim()) {
      const needle = itersQuery.toLowerCase()
      arr = arr.filter((it) =>
        JSON.stringify(it).toLowerCase().includes(needle),
      )
    }
    const dirMul = itersSort.dir === "asc" ? 1 : -1
    arr.sort((a, b) => {
      const key = itersSort.key
      if (key === "iteration")
        return dirMul * ((a.iteration ?? 0) - (b.iteration ?? 0))
      if (key === "nodesF1") return dirMul * (a._nodesF1 - b._nodesF1)
      if (key === "edgesF1") return dirMul * (a._edgesF1 - b._edgesF1)
      if (key === "nodeTypes")
        return dirMul * ((a._nodeTypes || 0) - (b._nodeTypes || 0))
      if (key === "edgesCnt")
        return dirMul * ((a._edgesCnt || 0) - (b._edgesCnt || 0))
      return 0
    })
    return arr
  }, [itersRaw, itersQuery, itersSort, run])

  const chartData = useMemo(() => {
    const its = itersFilteredSorted
    if (!its.length) return { f1Series: [], elapsed: [] }

    const runStart =
      run?.startTs ??
      run?.ts ??
      its[0]?.ts ??
      null

    const f1NodesPoints = its.map((it, idx) => ({
      iteration: it.iteration ?? idx + 1,
      value: Number(it.metrics?.nodes?.f1Score) || 0,
    }))

    const f1EdgesPoints = its.map((it, idx) => ({
      iteration: it.iteration ?? idx + 1,
      value: Number(it.metrics?.edges?.f1Score) || 0,
    }))

    const elapsed = its.map((it, idx) => {
      const ms =
        it.ts && runStart
          ? Math.max(0, Number(it.ts) - Number(runStart))
          : NaN

      return {
        iteration: it.iteration ?? idx + 1,
        ms,
      }
    })

    const f1Series = [
      {
        id: "nodesF1",
        label: "Nodes F1",
        color: "#10b981",
        points: f1NodesPoints,
      },
      {
        id: "edgesF1",
        label: "Edges F1",
        color: "#8b5cf6",
        points: f1EdgesPoints,
      },
    ]

    return { f1Series, elapsed }
  }, [itersFilteredSorted, run])

  if (!open || !run) return null

  const datasetLabel =
    datasetById[run._dataset]?.label || run._dataset || "Run"

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-[95vw] max-w-5xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              Iterations • {datasetLabel}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {run.id} • {formatDateTime(run.ts)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400 dark:text-gray-400" />
              <input
                value={itersQuery}
                onChange={(e) => setItersQuery(e.target.value)}
                placeholder="Search iterations…"
                className="h-9 pl-8 pr-2 border rounded bg-white text-gray-900 min-w-[260px] border-gray-300 placeholder-gray-400
                           dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:placeholder-gray-400"
              />
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() =>
                  setItersSort((s) => ({
                    key: "iteration",
                    dir:
                      s.key === "iteration" && s.dir === "asc"
                        ? "desc"
                        : "asc",
                  }))
                }
                className="h-9 px-3 rounded-md border bg-white hover:bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                title="Sort by iteration"
              >
                <ArrowUpDown className="h-4 w-4 inline mr-1" />
                Iter
              </button>
              <button
                onClick={() =>
                  setItersSort((s) => ({
                    key: "nodesF1",
                    dir:
                      s.key === "nodesF1" && s.dir === "asc"
                        ? "desc"
                        : "asc",
                  }))
                }
                className="h-9 px-3 rounded-md border bg-white hover:bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                title="Sort by Nodes F1"
              >
                <ArrowUpDown className="h-4 w-4 inline mr-1" />
                Node F1
              </button>
              <button
                onClick={() =>
                  setItersSort((s) => ({
                    key: "edgesF1",
                    dir:
                      s.key === "edgesF1" && s.dir === "asc"
                        ? "desc"
                        : "asc",
                  }))
                }
                className="h-9 px-3 rounded-md border bg-white hover:bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                title="Sort by Edges F1"
              >
                <ArrowUpDown className="h-4 w-4 inline mr-1" />
                Edge F1
              </button>
            </div>
            <button
              onClick={() => {
                const name = `${run._dataset || "dataset"}_${
                  run.id || "run"
                }_iterations.json`
                const blob = new Blob([JSON.stringify(itersRaw, null, 2)], {
                  type: "application/json",
                })
                const url = URL.createObjectURL(blob)
                const a = document.createElement("a")
                a.href = url
                a.download = name
                a.click()
                URL.revokeObjectURL(url)
              }}
              className="h-9 px-3 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200
                         dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              Export
            </button>
            <button
              onClick={onClose}
              className="h-9 px-3 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-0">
          {itersFilteredSorted.length === 0 ? (
            <div className="p-4 text-sm text-gray-600 dark:text-gray-300">
              No per-iteration data found for this run. If your backend can emit
              a JSON line per iteration (e.g.,{" "}
              <code>
                {'{iteration: 1, metrics: {...}, summary: {...}, ts: 1711111111}'}
              </code>
              ), it will be parsed and shown here.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <MultiMetricLineChart
                    series={chartData.f1Series}
                    title="F1 per iteration (nodes & edges)"
                    yFmt={(v) =>
                      (Math.round(v * 100) / 100).toFixed(2)
                    }
                    yLabel="F1-score"
                  />

                  <ScrollableBarChartWithAxes
                    data={chartData.elapsed.filter((d) => isFinite(d.ms))}
                    title="Elapsed time per iteration"
                    yFmt={(v) => {
                      if (!isFinite(v) || v <= 0) return "—"
                      if (v < 1000) return `${Math.round(v)} ms`
                      const s = v / 1000
                      if (s < 60) return `${s.toFixed(1)} s`
                      const m = Math.floor(s / 60)
                      const rs = Math.round(s % 60)
                      return `${m}m ${rs}s`
                    }}
                    yLabel="Elapsed time"
                  />
                </div>
              </div>

              <div className="overflow-y-auto max-h-[20vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                    <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-2">Iter</th>
                      <th className="px-3 py-2">Nodes F1 / P / R</th>
                      <th className="px-3 py-2">Edges F1 / P / R</th>
                      <th className="px-3 py-2">Node Types</th>
                      <th className="px-3 py-2">Edges</th>
                      <th className="px-3 py-2">Timestamp</th>
                      <th className="px-3 py-2">Δ Time</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itersFilteredSorted.map((it, idx) => {
                      const n = it.metrics?.nodes || {}
                      const e = it.metrics?.edges || {}
                      const nodeTypes =
                        it.summary?.nodeTypes ??
                        it.snapshot?.nodes?.length
                      const edgesCnt =
                        it.summary?.edges ?? it.snapshot?.edges?.length
                      const ts = it.ts ? formatDateTime(it.ts) : "—"

                      const baseTs =
                        idx === 0
                          ? run?.startTs ??
                            run?.ts ??
                            it.ts ??
                            null
                          : itersFilteredSorted[idx - 1]?.ts ?? null

                      const dt =
                        it.ts && baseTs
                          ? Math.max(
                              0,
                              Number(it.ts) - Number(baseTs),
                            )
                          : NaN

                      return (
                        <tr
                          key={it._rowId}
                          className="border-b border-gray-100 dark:border-gray-800"
                        >
                          <td className="px-3 py-2 font-medium">
                            {it.iteration ?? "—"}
                          </td>
                          <td className="px-3 py-2">
                            {num(n.f1Score)} / {num(n.precision)} /{" "}
                            {num(n.recall)}
                          </td>
                          <td className="px-3 py-2">
                            {num(e.f1Score)} / {num(e.precision)} /{" "}
                            {num(e.recall)}
                          </td>
                          <td className="px-3 py-2">
                            {nodeTypes ?? "—"}
                          </td>
                          <td className="px-3 py-2">
                            {edgesCnt ?? "—"}
                          </td>
                          <td className="px-3 py-2">{ts}</td>
                          <td className="px-3 py-2">
                            {isFinite(dt)
                              ? (() => {
                                  if (dt < 1000)
                                    return `${Math.round(dt)} ms`
                                  const s = dt / 1000
                                  if (s < 60)
                                    return `${s.toFixed(1)} s`
                                  const m = Math.floor(s / 60)
                                  const rs = Math.round(s % 60)
                                  return `${m}m ${rs}s`
                                })()
                              : "—"}
                          </td>
                          <td className="px-3 py-2">
                            {it.snapshot?.nodes || it.snapshot?.edges ? (
                              <button
                                className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                                onClick={() =>
                                  loadIterationToGraph(
                                    run?._dataset ||
                                      run?.params?.dataset ||
                                      "POLE",
                                    it,
                                  )
                                }
                                title="Load this iteration in Graph_Info"
                              >
                                Load in Graph_Info
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500">
                                No snapshot
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
